/**
 * DashboardCustomCSS — /website-builder/custom-css
 *
 * Per-page custom CSS editor with monospace textarea, live preview, and
 * page picker.
 *
 *   GET /api/builder/pages
 *   GET /api/builder/pages/{page_id}/custom-css
 *   PUT /api/builder/pages/{page_id}/custom-css   {css}
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Code2, FileText, Save, Loader2, ExternalLink, AlertTriangle, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SNIPPETS = [
  {
    label: 'Custom font on headings',
    css: `/* Use a different font for headings */
h1, h2, h3 {
  font-family: 'Playfair Display', Georgia, serif;
  letter-spacing: -0.01em;
}`,
  },
  {
    label: 'Subtle background texture',
    css: `/* Add a grain texture to all sections */
[data-block-id] {
  background-image: radial-gradient(rgba(0,0,0,0.04) 1px, transparent 1px);
  background-size: 16px 16px;
}`,
  },
  {
    label: 'Highlight first button',
    css: `/* Make the first button on the page pop */
[data-block-id] a[role="button"]:first-of-type,
[data-block-id] button:first-of-type {
  box-shadow: 0 8px 30px rgba(139,92,246,0.35);
  transform: scale(1.04);
}`,
  },
];

export default function DashboardCustomCSS() {
  const [pages, setPages]   = useState([]);
  const [activePage, setActive] = useState(null);
  const [css, setCss]       = useState('');
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [dirty,   setDirty]   = useState(false);

  useEffect(() => {
    axios.get(`${API}/builder/pages`, { headers: headers() })
      .then((r) => {
        const items = r.data?.items || [];
        setPages(items);
        if (items.length && !activePage) setActive(items[0]);
      })
      .catch(() => toast.error('Failed to load pages'))
      .finally(() => setLoading(false));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (!activePage) return;
    axios.get(`${API}/builder/pages/${activePage.page_id}/custom-css`, { headers: headers() })
      .then((r) => { setCss(r.data?.css || ''); setDirty(false); })
      .catch(() => toast.error('Failed to load CSS'));
  }, [activePage]);

  const save = async () => {
    if (!activePage) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/builder/pages/${activePage.page_id}/custom-css`,
        { css },
        { headers: headers() },
      );
      setCss(r.data?.css || '');
      setDirty(false);
      toast.success('Custom CSS saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const byteCount = new Blob([css || '']).size;
  const overLimit = byteCount > 100_000;

  return (
    <div className="p-10 max-w-7xl" data-testid="custom-css-page">
      <div className="flex items-start justify-between gap-4 mb-7 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#A78BFA] font-bold mb-2 flex items-center gap-1.5"><Code2 size={12} /> Website · Custom CSS</p>
          <h1 className="text-3xl font-bold text-white font-syne tracking-tight">Custom CSS</h1>
          <p className="text-[#A5A8C9] text-sm mt-1.5">Inject custom CSS scoped to the selected page. Great for one-off tweaks, custom fonts, and brand polish.</p>
        </div>
        <button
          onClick={save}
          disabled={saving || overLimit || !activePage}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white text-[11px] font-bold uppercase tracking-wider hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] transition-all disabled:opacity-50 flex items-center gap-2"
          data-testid="custom-css-save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Page picker */}
        <div className="space-y-3">
          <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold px-2 pt-1 pb-2">Pages</p>
            <div className="space-y-1">
              {loading ? (
                <div className="px-2 py-3 text-[12px] text-white/55 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
              ) : pages.map((p) => (
                <button
                  key={p.page_id}
                  onClick={async () => {
                    if (dirty && !(await themedConfirm('You have unsaved CSS changes. Discard?'))) return;
                    setActive(p);
                  }}
                  className={`w-full text-left px-3.5 py-2.5 rounded-lg transition-all flex items-center gap-2.5 ${
                    activePage?.page_id === p.page_id
                      ? 'bg-violet-500/15 border border-violet-400/30'
                      : 'border border-transparent hover:bg-white/[0.04]'
                  }`}
                  data-testid={`custom-css-page-${p.page_id}`}
                >
                  <FileText size={13} className={activePage?.page_id === p.page_id ? 'text-violet-300' : 'text-white/55'} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[12.5px] font-semibold truncate ${activePage?.page_id === p.page_id ? 'text-white' : 'text-white/80'}`}>{p.title || p.page_id}</p>
                    <p className="text-[10.5px] text-white/45 truncate font-mono">/{p.page_id === 'home' ? '' : p.page_id}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Snippet library */}
          <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2 flex items-center gap-1.5"><Sparkles size={11} /> Quick snippets</p>
            <div className="space-y-2">
              {SNIPPETS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setCss((c) => `${c ? c + '\n\n' : ''}${s.css}\n`); setDirty(true); }}
                  className="w-full text-left px-3 py-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.06] border border-white/[0.04] transition-all"
                  data-testid={`custom-css-snippet-${i}`}
                >
                  <p className="text-[12px] font-semibold text-white">{s.label}</p>
                  <p className="text-[10px] text-white/45 mt-0.5">Click to insert</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="space-y-4">
          {!activePage ? (
            <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-12 text-center">
              <FileText size={28} className="text-white/30 mx-auto mb-3" />
              <p className="text-white/55">Pick a page to add custom CSS.</p>
            </div>
          ) : (
            <>
              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold mb-1">Editing CSS for</p>
                  <h2 className="text-xl font-bold text-white font-syne">{activePage.title || activePage.page_id}</h2>
                </div>
                {activePage.url && (
                  <a href={activePage.url} target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-wider font-bold text-violet-300 hover:text-white flex items-center gap-1.5">
                    <ExternalLink size={11} /> View live page
                  </a>
                )}
              </div>

              <div className="bg-[#0F0F22] border border-white/[0.08] rounded-2xl overflow-hidden">
                <div className="px-4 py-2 border-b border-white/[0.06] flex items-center justify-between bg-[#11112A]">
                  <span className="text-[10.5px] font-mono text-white/45">custom.css · {activePage.page_id}</span>
                  <span className={`text-[10.5px] font-mono ${overLimit ? 'text-red-300' : 'text-white/45'}`} data-testid="custom-css-byte-count">
                    {(byteCount / 1024).toFixed(1)} / 100 KB
                  </span>
                </div>
                <textarea
                  value={css}
                  onChange={(e) => { setCss(e.target.value); setDirty(true); }}
                  placeholder={`/* Write CSS that applies only to ${activePage.title || activePage.page_id}. */
/* Example:                                          */
/*   h1 { color: #8B5CF6; }                          */
/*   .hero-button { transform: scale(1.05); }        */`}
                  rows={20}
                  spellCheck={false}
                  className="w-full bg-[#0F0F22] text-white text-[13px] font-mono p-5 border-0 outline-none resize-y leading-[1.55]"
                  data-testid="custom-css-textarea"
                  style={{ tabSize: 2 }}
                />
              </div>

              {overLimit && (
                <div className="bg-red-500/10 border border-red-400/20 rounded-xl p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="text-red-300 mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-red-200">CSS exceeds the 100 KB limit. Trim it down before saving.</p>
                </div>
              )}

              <div className="bg-[#15152E] border border-white/[0.06] rounded-2xl p-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 font-bold mb-2">Heads-up</p>
                <ul className="space-y-1.5 text-[12px] text-white/65">
                  <li>• CSS is scoped to this page only — other pages keep their own styles.</li>
                  <li>• <code className="text-violet-300 font-mono">@import url(…)</code>, <code className="text-violet-300 font-mono">expression()</code>, <code className="text-violet-300 font-mono">javascript:</code>, and <code className="text-violet-300 font-mono">&lt;script&gt;</code> are blocked at save time.</li>
                  <li>• Changes load on next page-view (the canvas editor refreshes when you switch pages).</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
